import React, {useLayoutEffect, useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactECharts from "echarts-for-react";
import { ResilientWebSocket } from "./resilientSocket";
import { useAccount } from "./account-context"; 
import { ACCOUNT_CODES } from "./constants/healthEndpoints";  
import { useChartTheme, withChartTheme } from "./charts/theme";

dayjs.extend(relativeTime);

export default function OperationalInsights() {

  const chartTheme = useChartTheme();
    const { accountId, userEmail, language, setLanguage } = useAccount();
   // console.log("OperationalInsights - Account ID:", ACCOUNT_CODES[accountId], accountId);
  const market = ACCOUNT_CODES[accountId] || "PT";
  const wsurl = "wss://ea5tuf0x77.execute-api.us-east-1.amazonaws.com/staging";
  const token = "297ad484-c6d4-4d55-849b-6fe4b20c673f"
  const timeout = 60;

  const chatWindowRef = useRef(null);
  const socketRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");

  const [chartOpen, setChartOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({});
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [insightHtml, setInsightHtml] = useState("");
  const endRef = useRef(null);
//const { accountId, userEmail, language, setLanguage } = useAccount();
  const [chartOptions, setChartOptions] = useState({
    tooltip: { trigger: "axis" },
    legend: {
      top: 20,
      left: "center",
      orient: "horizontal",
    },
    grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
    xAxis: {
      type: "time",
      minInterval: 4 * 24 * 60 * 60 * 1000,
      splitNumber: 10,
      axisLabel: { formatter: "{M}/{d}" },
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: "{value}%" },
    },
    series: [],
  });

  useLayoutEffect(() => {
    scrollToBottom();
    }, [messages, isTyping]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chartInstance = chartRef.current.getEchartsInstance();
    const container = chartRef.current.ele;

    if (!container || !chartInstance) return;

    const observer = new ResizeObserver(() => {
      chartInstance.resize();
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  /* ---------------- Time ticker ---------------- */
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  /* ---------------- WebSocket ---------------- */
  useEffect(() => {
    socketRef.current = new ResilientWebSocket({
      url: wsurl + '?token=' + token,
      onMessage: (event) => {
        try {
          if (!event || event.role !== "assistant") return;

          setIsTyping(false);

          const requestId = event.requestId;
          setPendingRequests((prev) => {
            if (!prev[requestId]) return prev;
            clearTimeout(prev[requestId].timer);
            const copy = { ...prev };
            delete copy[requestId];
            return copy;
          });

          let parsed;
          try {
            parsed = JSON.parse(event.message);
          } catch {
            parsed = event.message;
          }

          const text = parsed?.message ?? parsed;

          if (text) {
            setMessages((m) => [
              ...m,
              { role: "assistant", text, timestamp: Date.now() },
            ]);
          }

          scrollToBottom();
        } catch {
          fallbackError();
        }
      },
      onStatus: (status) => {
        setConnectionStatus(status);
        setConnected(status === "connected");
      },
    });

    return () => socketRef.current?.close?.();
  }, []);

  /* ---------------- Initial insights HTML ---------------- */
  useEffect(() => {
    setNow(Date.now());
    }, [messages]);

  useEffect(() => {
    
    const url =
      `https://testwidget11.s3.amazonaws.com/${market}/insights.json?v=${Date.now()}`;

    axios.get(url).then((res) => {
      const html = res?.data?.data ?? "";
      setInsightHtml(html.replace(/<h3/g, "<h4"));
    });
  }, [accountId]);

  /* ---------------- Chart data ---------------- */
  useEffect(() => {
    async function loadChart() {
      const keys = [
        ["DigitalOrdering", "circle"],
        ["Sesame", "triangle"],
        ["eProd", "rect"],
        ["MyStore", "diamond"],
      ];

      const datasets = {};
      await Promise.all(
        keys.map(async ([key]) => {
          const res = await axios.get(
            `https://testwidget11.s3.amazonaws.com/${market}/${key}.json`,
          );
          datasets[key] = res.data;
        }),
      );

      setChartOptions((prev) => ({
        ...prev,
        legend: {
          ...prev.legend,
          data: keys.map((k) => k[0]),
        },
        series: keys.map(([key, symbol]) => ({
          name: key,
          type: "line",
          symbol,
          data: datasets[key].map((x) => [x.timestamp, x.value]),
        })),
      }));
    }

    loadChart().catch(console.error);
  }, []);

  /* ---------------- Helpers ---------------- */
    const scrollToBottom = () => {
      const el = chatWindowRef.current;
        if (!el) return;
            el.scrollTop = el.scrollHeight;
            };



  const fallbackError = () => {
    setIsTyping(false);
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        text: "Apologies, please try again later",
        timestamp: Date.now(),
      },
    ]);
  };

  const relative = (ts) => dayjs(ts).from(now);

  /* ---------------- Send message ---------------- */
  const sendMessage = async () => {
    if (!connected) {
      fallbackError();
      return;
    }

    if (!input.trim()) return;
    if (input.trim().length <= 3) return;

    const userMessage = {
      role: "user",
      text: input,
      timestamp: Date.now(),
    };
    setInput("");

    setMessages((m) => [...m, userMessage]);
    setIsTyping(true);
    scrollToBottom();

    const requestId = crypto.randomUUID();

    const timer = setTimeout(() => {
      setIsTyping(false);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Request timed out. Please try again.",
          timestamp: Date.now(),
        },
      ]);
    }, timeout * 1000);

    setPendingRequests((p) => ({
      ...p,
      [requestId]: { startedAt: Date.now(), timer },
    }));

    const payload = {
      action: "sendMessage",
      market,
      requestId,
      messages: [...messages, userMessage].map((m) => ({
        role: m.role,
        content: [
          {
            type: m.role === "assistant" ? "output_text" : "input_text",
            text: m.text,
          },
        ],
      })),
    };

    socketRef.current.send(payload);
  };
  const chartRef = useRef(null);

  // This effect runs whenever messages update
// useEffect(() => {
//   const lastMessage = messages[messages.length - 1];
//   if (lastMessage?.role !== "assistant") return;

//   // Get CSS variable
//   let value = getComputedStyle(document.documentElement)
//     .getPropertyValue("--chat-height")
//     .trim();

//   // Detect unit
//   const isRem = value.endsWith("rem");
//   const isPx = value.endsWith("px");

//   let currentPx;

//   if (isRem) {
//     const remValue = parseFloat(value.replace("rem", ""));
//     const rootFont = parseFloat(
//       getComputedStyle(document.documentElement).fontSize
//     ); // usually 16px
//     currentPx = remValue * rootFont;
//   } else if (isPx) {
//     currentPx = parseFloat(value.replace("px", ""));
//   } else {
//     // fallback
//     currentPx = 320;
//   }

//   // Increase height by 10%
//   const grownPx = currentPx * 1.10;

//   // Max-height = calc(100vh - 25rem)
//   const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize);
//   const maxHeightPx = window.innerHeight - (25 * rootFont);

//   const finalPx = Math.min(grownPx, maxHeightPx);

//   // Convert px back to rem before saving
//   const finalRem = finalPx / rootFont;

//   // Update CSS variable in rem
//   document.documentElement.style.setProperty("--chat-height", `${finalRem}rem`);
// }, [messages]);

// useEffect(() => {
//   const last = messages[messages.length - 1];
//   if (!last || last.role !== "assistant") return;

//   const chat = chatWindowRef.current;
//   if (!chat) return;

//   // Actual content height
//   const currentHeight = chat.offsetHeight;
//   const scrollHeight = chat.scrollHeight;

//   // Increase height by 10%, but not more than content height
//   const grownPx = Math.min(scrollHeight, currentHeight * 1.10);

//   // Cap at viewport constraint
//   const rootFont = parseFloat(
//     getComputedStyle(document.documentElement).fontSize
//   );
//   const maxHeightPx = window.innerHeight - (25 * rootFont);

//   const finalPx = Math.min(grownPx, maxHeightPx);

//   const finalRem = finalPx / rootFont;

//   document.documentElement.style.setProperty(
//     "--chat-height",
//     `${finalRem}rem`
//   );

//   // Scroll AFTER height finishes updating
//   setTimeout(() => {
//     endRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, 60);

// }, [messages]);

useEffect(() => {
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") return;

  const chat = chatWindowRef.current;
  if (!chat) return;

  const rootFont = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );

  // 1️⃣ Read CSS variable safely
  let cssHeight = getComputedStyle(document.documentElement)
    .getPropertyValue("--chat-height")
    .trim();

  // 2️⃣ If missing, initialize it to 20rem
  if (!cssHeight || cssHeight === "" || cssHeight === "undefined") {
    document.documentElement.style.setProperty("--chat-height", "20rem");
    cssHeight = "20rem";
  }

  // 3️⃣ Convert CSS var to px
  let currentMaxHeightPx = 0;

  if (cssHeight.endsWith("rem")) {
    currentMaxHeightPx =
      parseFloat(cssHeight.replace("rem", "")) * rootFont;
  } else if (cssHeight.endsWith("px")) {
    currentMaxHeightPx = parseFloat(cssHeight.replace("px", ""));
  } else {
    currentMaxHeightPx = 20 * rootFont; // fallback 20rem
  }

  // 4️⃣ Check full content height
  //const contentHeightPx = chat.scrollHeight;

  // 5️⃣ Increase height by 10%
  const grownPx = currentMaxHeightPx * 1.1;
  console.log("Grown px:", grownPx);

  // 6️⃣ Respect content height and viewport limit
  // const maxHeightPx = window.innerHeight - (25 * rootFont);
  // const finalPx = Math.min(grownPx, contentHeightPx, maxHeightPx);

  // 7️⃣ Convert back to rem
  //const finalRem = finalPx / rootFont;
const finalRem = grownPx / 16;
  console.log("Final rem:", finalRem);

  if (!isNaN(finalRem) && finalRem > 0) {
    document.documentElement.style.setProperty(
      "--chat-height",
      `${finalRem}rem`
    );
  }

}, [messages]);
// useEffect(() => {
//   endRef.current?.scrollIntoView({ behavior: "smooth" });
// }, [messages, isTyping]);

// console.log("Account ID:", accountId);
// console.log("User Email:", userEmail);
  /* ---------------- Render ---------------- */
  const themedOption = useMemo(
    () => withChartTheme(chartOptions, chartTheme),
    [chartOptions, chartTheme]
  );

  return (
    <div className="page insights">
      <div className="header">Operational Insights – AI Assistant</div>

      <div className="content">
        <div className="panel insights">
          <h3>Operational Insights</h3>

          <div
            className="collapse-header"
            onClick={() => setChartOpen((v) => !v)}
          >
            <span className="opi-chevron">Historical Trendline (All Stores) {chartOpen ? "▾" : "▸"}</span>
           
          </div>

          <div
            className="collapse-body"
            style={{
              maxHeight: chartOpen ? "25rem" : "0px",
            }}
          >
            <div className="chart-slot">
              <ReactECharts
                option={themedOption}
                notMerge={true}
                lazyUpdate={true}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          </div>

          <div
            className="insights-card"
            dangerouslySetInnerHTML={{ __html: insightHtml }}
          />
        </div>

        <div className="panel ai">
          <h3>Ask the RMP AI Assistant</h3>

          <div className="chat-window" ref={chatWindowRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-bubble ${m.role}`}>
                <div className="sender">
                  {m.role === "user" ? "You" : "RMP AI Assistant"}
                </div>
                <div
                  className="text"
                  dangerouslySetInnerHTML={
                    m.role === "assistant" ? { __html: m.text } : undefined
                  }
                >
                  {m.role === "user" ? m.text : null}
                </div>
                <div className="timestamp">{relative(m.timestamp)}</div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-bubble ai typing">
                <div className="sender">RMP AI Assistant</div>
                <div className="typing-indicator">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
              <div className="ref" ref={endRef} />
          </div>

          <div className="chat-input">
          <textarea
            value={input}
              onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                  onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                                  sendMessage();
                                      }
                                        }}
                                        />

            <button onClick={sendMessage} disabled={input.length < 4}>
              Send
            </button>
          </div>
          {messages.length === 0 && !isTyping && (
            <div className="chat-hint">
              <div>Try asking:</div>
              <div>• What changed in the last week?</div>
              <div>• Which stores are most at risk?</div>
              <div>• What should I do next?</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
